import styles from './Filters.module.scss';
import React, { FC, useRef, useState, useEffect, useCallback } from 'react';
import iconFilter from '../img/filters/iconFilter.svg';
import { ReactComponent as IconCloseBlue } from '../img/filters/iconCloseBlue.svg';
import { ReactComponent as IconChevron } from '../../../assets/img/filter/iconChevronDown.svg';
import { ReactComponent as IconChevronUp } from '../../../assets/img/filter/iconChevronUp.svg';
import { ReactComponent as IconTypeContract } from '../img/filters/iconTypeContract.svg';
import { ReactComponent as IconCalculator } from '../img/filters/iconCalculator.svg';
import { ReactComponent as IconPlanet } from '../img/filters/iconPlanet.svg';
import { ReactComponent as IconStatus } from '../img/filters/iconStatus.svg';
import { ReactComponent as IconMVD } from '../img/filters/iconMVD.svg';
import { ReactComponent as IconDone } from '../img/iconDone.svg';
import { Button } from 'ui/Button';
import { DatesRangeValue } from '@mantine/dates';
import { ContractsPriceFilter } from 'components/ContractsFilters/ContractsPriceFilter';
import {
  ContractsAdaptiveFilter,
  SelectedItem,
} from 'components/ContractsFilters/ContractsAdaptiveFilter';
import { ReactComponent as IconCategory } from '../../EmployedsContent/img/filters/iconCategory.svg';
import { ChildrenType } from 'redux/Api/contractsApi/type';
import { useGetCategoryEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';

function formatDateForAPI(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface CategoryItem {
  id: string;
  name: string;
}

export interface ContractsFilterParams {
  categories?: CategoryItem[];
  types?: string[];
  statuses?: string[];
  nationalities?: string[];
  mvd_notifications?: string[];
  limit_from?: number;
  limit_to?: number;
  date_add?: { start: string; end: string };
}

interface PropsType {
  dataFilters: {
    nationalities: ChildrenType[];
    types?: ChildrenType[];
    mvd_notifications: ChildrenType[];
    statuses: ChildrenType[];
  };
  initialFilters?: ContractsFilterParams;
  onApplyFilters: (filters: ContractsFilterParams) => void;
  onResetFilters: () => void;
  isLoading?: boolean;
}

export interface AdaptiveFilterOption {
  keyword: string;
  title: string;
}

export const Filters: FC<PropsType> = ({
  dataFilters,
  initialFilters = {},
  onApplyFilters,
  onResetFilters,
  isLoading = false,
}) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const filtersWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filtersWrapperRef.current &&
        !filtersWrapperRef.current.contains(event.target as Node)
      ) {
        setIsFiltersOpen(false);
        setOpenSection(null);
      }
    };

    if (isFiltersOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFiltersOpen]);

  useEffect(() => {
    if (!isFiltersOpen) {
      setOpenSection(null);
    }
  }, [isFiltersOpen]);

  const [nationalityKeywords, setNationalityKeywords] = useState<string[]>(
    initialFilters.nationalities ?? [],
  );
  const [typeKeywords, setTypeKeywords] = useState<string[]>(initialFilters.types ?? []);
  const [categoriesKeywords, setCategoriesKeywords] = useState<CategoryItem[]>(
    initialFilters.categories ?? [],
  );
  const [statusKeywords, setStatusKeywords] = useState<string[]>(
    initialFilters.statuses ?? [],
  );
  const [mvdKeywords, setMvdKeywords] = useState<string[]>(
    initialFilters.mvd_notifications ?? [],
  );

  const [limitFrom, setLimitFrom] = useState<number | undefined>(
    initialFilters.limit_from,
  );
  const [limitTo, setLimitTo] = useState<number | undefined>(initialFilters.limit_to);
  const [dateAddRange, setDateAddRange] = useState<DatesRangeValue>([
    initialFilters.date_add?.start ? new Date(initialFilters.date_add.start) : null,
    initialFilters.date_add?.end ? new Date(initialFilters.date_add.end) : null,
  ]);

  useEffect(() => {
    setNationalityKeywords(initialFilters.nationalities ?? []);
    setTypeKeywords(initialFilters.types ?? []);
    setCategoriesKeywords(initialFilters.categories ?? []);
    setStatusKeywords(initialFilters.statuses ?? []);
    setMvdKeywords(initialFilters.mvd_notifications ?? []);
    setLimitFrom(initialFilters.limit_from);
    setLimitTo(initialFilters.limit_to);
    setDateAddRange([
      initialFilters.date_add?.start ? new Date(initialFilters.date_add.start) : null,
      initialFilters.date_add?.end ? new Date(initialFilters.date_add.end) : null,
    ]);
  }, [
    initialFilters.nationalities,
    initialFilters.types,
    initialFilters.categories,
    initialFilters.statuses,
    initialFilters.mvd_notifications,
    initialFilters.limit_from,
    initialFilters.limit_to,
    initialFilters.date_add?.start,
    initialFilters.date_add?.end,
  ]);

  const toggleSection = (key: string) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  const [queryParamsCategorys, setQueryParamsCategorys] = useState({
    page: 1,
    search: '',
  });

  const handleCategorysServerSearch = useCallback((search: string) => {
    setQueryParamsCategorys((prev) => {
      if (prev.page === 1 && prev.search === search) return prev;
      return {
        ...prev,
        page: 1,
        search,
      };
    });
  }, []);

  const {
    data: categorysFiltersData,
    isLoading: isLoadingCategorys,
    isFetching: isFetchingCategorys,
  } = useGetCategoryEmployedsQuery(
    {
      page: queryParamsCategorys.page,
      ...(queryParamsCategorys.search ? { search: queryParamsCategorys.search } : {}),
    },
    { skip: !isFiltersOpen },
  );

  const hasNextCategoryPage =
    !!categorysFiltersData?.meta &&
    categorysFiltersData.meta.current_page < categorysFiltersData.meta.last_page;

  const loadMoreCategories = useCallback(() => {
    if (isFetchingCategorys) return;
    if (!hasNextCategoryPage) return;
    setQueryParamsCategorys((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [isFetchingCategorys, hasNextCategoryPage]);

  const transformNationalities = (): AdaptiveFilterOption[] => {
    return (dataFilters?.nationalities || []).map((national) => ({
      keyword: national.keyword || '',
      title: national.title,
    }));
  };

  const transformStatus = (): AdaptiveFilterOption[] => {
    return (dataFilters?.statuses || []).map((status) => {
      return {
        keyword: status.keyword || '',
        title: status.title,
      };
    });
  };

  const transformCategories = (): AdaptiveFilterOption[] => {
    return (categorysFiltersData?.data || []).map((type) => ({
      keyword: type.id.toString() || '',
      title: type.name || '',
    }));
  };

  const transformType = (): AdaptiveFilterOption[] => {
    return (dataFilters?.types ?? []).map((type) => ({
      keyword: type.keyword || '',
      title: type.title,
    }));
  };

  const hasTypesFilter = (dataFilters?.types?.length ?? 0) > 0;

  const transformMvd = (): AdaptiveFilterOption[] => {
    return (dataFilters?.mvd_notifications || []).map((mvd_notification) => ({
      keyword: mvd_notification.keyword || '',
      title: mvd_notification.title,
    }));
  };

  const hasAppliedFilters = (): boolean => {
    return (
      (initialFilters.nationalities?.length ?? 0) > 0 ||
      (initialFilters.categories?.length ?? 0) > 0 ||
      (initialFilters.statuses?.length ?? 0) > 0 ||
      (initialFilters.types?.length ?? 0) > 0 ||
      (initialFilters.mvd_notifications?.length ?? 0) > 0 ||
      initialFilters.limit_from != null ||
      initialFilters.limit_to != null ||
      initialFilters.date_add?.start != null ||
      initialFilters.date_add?.end != null
    );
  };

  const countAppliedFilters = (): number => {
    let count = 0;
    if (initialFilters.nationalities?.length) count++;
    if (initialFilters.categories?.length) count++;
    if (initialFilters.statuses?.length) count++;
    if (initialFilters.types?.length) count++;
    if (initialFilters.mvd_notifications?.length) count++;
    if (initialFilters.limit_from != null || initialFilters.limit_to != null) count++;
    if (initialFilters.date_add?.start != null || initialFilters.date_add?.end != null)
      count++;
    return count;
  };

  const isNationalityFilterApplied = (): boolean =>
    (nationalityKeywords?.length ?? 0) > 0;
  const isTypeFilterApplied = (): boolean => (typeKeywords?.length ?? 0) > 0;
  const isLimitFilterApplied = (): boolean => limitFrom != null || limitTo != null;
  const isMvdFilterApplied = (): boolean => (mvdKeywords?.length ?? 0) > 0;
  const isStatusFilterApplied = (): boolean => (statusKeywords?.length ?? 0) > 0;
  const isCategoriesFilterApplied = (): boolean => (categoriesKeywords?.length ?? 0) > 0;

  const handleCategoriesChange = (values: string[] | SelectedItem[]) => {
    setCategoriesKeywords(values as CategoryItem[]);
  };

  const handleTypeChange = (values: string[] | SelectedItem[]) => {
    setTypeKeywords(values as string[]);
  };

  const handleStatusChange = (values: string[] | SelectedItem[]) => {
    setStatusKeywords(values as string[]);
  };

  const handleNationalityChange = (values: string[] | SelectedItem[]) => {
    setNationalityKeywords(values as string[]);
  };

  const handleMvdChange = (values: string[] | SelectedItem[]) => {
    setMvdKeywords(values as string[]);
  };

  const handleCloseFilters = () => {
    setIsFiltersOpen(false);
    setOpenSection(null);
  };

  const handleApply = () => {
    const start = dateAddRange[0];
    const end = dateAddRange[1] ?? start;
    const dateAdd =
      start != null && end != null
        ? {
            start: formatDateForAPI(start),
            end: formatDateForAPI(end),
          }
        : undefined;
    onApplyFilters({
      types: typeKeywords,
      statuses: statusKeywords,
      mvd_notifications: mvdKeywords,
      nationalities: nationalityKeywords,
      categories: categoriesKeywords,
      limit_from: limitFrom,
      limit_to: limitTo,
      date_add: dateAdd,
    });
    handleCloseFilters();
  };

  const handleReset = () => {
    if (isLoading) return;
    setNationalityKeywords([]);
    setTypeKeywords([]);
    setCategoriesKeywords([]);
    setStatusKeywords([]);
    setMvdKeywords([]);
    setLimitFrom(undefined);
    setLimitTo(undefined);
    setDateAddRange([null, null]);
    onResetFilters();
    handleCloseFilters();
  };

  return (
    <div style={{ display: 'flex', gap: '12px' }}>
      <div ref={filtersWrapperRef}>
        <button
          className={`${styles.filterButton} ${hasAppliedFilters() ? styles.active : ''}`}
          onClick={() => setIsFiltersOpen((prev) => !prev)}>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {!hasAppliedFilters() && (
              <img src={iconFilter} alt='Фильтр' className={styles.filterIcon} />
            )}

            <span className={hasAppliedFilters() ? styles.active : ''}>Фильтр</span>
            {hasAppliedFilters() && countAppliedFilters() > 1 ? (
              <div className={styles.filterCountBadge}>{countAppliedFilters()}</div>
            ) : null}
            {hasAppliedFilters() && (
              <IconCloseBlue
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleReset();
                }}
              />
            )}
          </div>
        </button>
        {isFiltersOpen && (
          <div className={styles.filtersDropdown} ref={filtersRef}>
            <span className={styles.filtersTitle}>Фильтр</span>
            <div className={styles.sections}>
              {hasTypesFilter && (
                <div
                  className={`${styles.section} ${
                    openSection === 'serviceType' ? styles.sectionActive : ''
                  }`}>
                  <button
                    type='button'
                    className={styles.sectionHeader}
                    onClick={() => toggleSection('serviceType')}>
                    <div className={styles.sectionHeaderContent}>
                      <IconTypeContract className={styles.sectionIcon} />
                      <div className={styles.textActions}>
                        <span>Тип договора</span>
                        {isTypeFilterApplied() && <span className={styles.activeDot} />}
                      </div>
                    </div>
                    <span className={styles.sectionArrow}>
                      {openSection === 'serviceType' ? (
                        <IconChevronUp />
                      ) : (
                        <IconChevron />
                      )}
                    </span>
                  </button>

                  {openSection === 'serviceType' && (
                    <div className={styles.sectionBody}>
                      <ContractsAdaptiveFilter
                        options={transformType()}
                        selectedValues={typeKeywords}
                        onChange={handleTypeChange}
                        searchPlaceholder='Искать...'
                        showSearch={false}
                      />
                    </div>
                  )}
                </div>
              )}

              <div
                className={`${styles.section} ${
                  openSection === 'categoriesType' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('categoriesType')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconCategory className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>Категории</span>
                      {isCategoriesFilterApplied() && (
                        <span className={styles.activeDot} />
                      )}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'categoriesType' ? (
                      <IconChevronUp />
                    ) : (
                      <IconChevron />
                    )}
                  </span>
                </button>

                {openSection === 'categoriesType' && (
                  <div className={styles.sectionBody}>
                    <ContractsAdaptiveFilter
                      options={transformCategories()}
                      selectedValues={categoriesKeywords}
                      onChange={handleCategoriesChange}
                      searchPlaceholder='Искать...'
                      serverSearch={true}
                      onServerSearchChange={handleCategorysServerSearch}
                      isFetchingTypes={isFetchingCategorys}
                      hasNextPage={hasNextCategoryPage}
                      isFetchingMore={isFetchingCategorys}
                      onLoadMore={loadMoreCategories}
                      storeFullObject={true}
                      isLoadingCategorys={isLoadingCategorys}
                    />
                  </div>
                )}
              </div>

              <div
                className={`${styles.section} ${
                  openSection === 'statuses' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('statuses')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconStatus className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>Статус</span>
                      {isStatusFilterApplied() && <span className={styles.activeDot} />}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'statuses' ? <IconChevronUp /> : <IconChevron />}
                  </span>
                </button>

                {openSection === 'statuses' && (
                  <div className={styles.sectionBody}>
                    <ContractsAdaptiveFilter
                      options={transformStatus()}
                      selectedValues={statusKeywords}
                      onChange={handleStatusChange}
                      searchPlaceholder='Искать...'
                      showSearch={false}
                    />
                  </div>
                )}
              </div>

              <div
                className={`${styles.section} ${
                  openSection === 'date' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('date')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconCalculator className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>Лимит</span>
                      {isLimitFilterApplied() && <span className={styles.activeDot} />}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'date' ? <IconChevronUp /> : <IconChevron />}
                  </span>
                </button>

                {openSection === 'date' && (
                  <div className={styles.sectionBody}>
                    <ContractsPriceFilter
                      show={true}
                      minValue={limitFrom}
                      maxValue={limitTo}
                      onChange={(min, max) => {
                        setLimitFrom(min);
                        setLimitTo(max);
                      }}
                      onReset={() => {
                        setLimitFrom(undefined);
                        setLimitTo(undefined);
                      }}
                      onApply={handleApply}
                    />
                  </div>
                )}
              </div>

              <div
                className={`${styles.section} ${
                  openSection === 'amount' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('amount')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconPlanet className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>Гражданство</span>
                      {isNationalityFilterApplied() && (
                        <span className={styles.activeDot} />
                      )}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'amount' ? <IconChevronUp /> : <IconChevron />}
                  </span>
                </button>

                {openSection === 'amount' && (
                  <div className={styles.sectionBody}>
                    <ContractsAdaptiveFilter
                      options={transformNationalities()}
                      selectedValues={nationalityKeywords}
                      onChange={handleNationalityChange}
                      searchPlaceholder='Искать...'
                    />
                  </div>
                )}
              </div>

              <div
                className={`${styles.section} ${
                  openSection === 'executor' ? styles.sectionActive : ''
                }`}>
                <button
                  type='button'
                  className={styles.sectionHeader}
                  onClick={() => toggleSection('executor')}>
                  <div className={styles.sectionHeaderContent}>
                    <IconMVD className={styles.sectionIcon} />
                    <div className={styles.textActions}>
                      <span>МВД</span>
                      {isMvdFilterApplied() && <span className={styles.activeDot} />}
                    </div>
                  </div>
                  <span className={styles.sectionArrow}>
                    {openSection === 'executor' ? <IconChevronUp /> : <IconChevron />}
                  </span>
                </button>

                {openSection === 'executor' && (
                  <div className={styles.sectionBody}>
                    <ContractsAdaptiveFilter
                      options={transformMvd()}
                      selectedValues={mvdKeywords}
                      onChange={handleMvdChange}
                      searchPlaceholder='Искать...'
                      showSearch={false}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className={styles.footer}>
              <Button
                className={styles.resetButton}
                theme='outline'
                onClick={handleReset}>
                <span
                  style={{
                    marginRight: '6px',
                  }}>
                  Сбросить
                </span>
              </Button>
              <Button className={styles.btnDone} onClick={handleApply} theme='filed'>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginLeft: '6px',
                  }}>
                  <span>Применить</span> <IconDone style={{ width: 20, height: 20 }} />
                </div>
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
